/* global window */
import React, { useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@fluentui/react-components";
import { useDispatch, useSelector, useStore } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { normalize, select, hover } from "../../state/selectionSlice";
import { getPinsForMoving } from "../../helpers/grid";
import { findBorderOnClick, isMouseClickedOnBorder } from "../../helpers/selection";
import CellShape from "./CellShape";
import CellCustomImage from "./CellCustomImage";
import CellTextWithIcon from "./CellTextWithIcon";
import CellShapeElement from "./CellShapeElement";
import { useDeckAIComms } from "../providers/DeckAIComms";
import { setLastOpenAccordionItem } from "../../state/appStateSlice";

const useClasses = makeStyles({
  input: {
    width: "95%",
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
    border: "none",
    outline: "none",
  },
  button: {
    width: "100%",
    height: "100%",
    alignContent: "center",
    textAlign: "center",
    ":focus-visible": {
      outline: "0px",
    },
    userSelect: "none",
  },
  cellContent: {
    "& ol, & ul": {
      listStylePosition: "inside",
      paddingLeft: 0,
      margin: 0,
      width: "fit-content",
      marginLeft: "auto",
      marginRight: "auto",
    },
    "& li": {
      textAlign: "left",
      listStylePosition: "inside",
      paddingLeft: 0,
    },
  },
});

const Cell = (props) => {
  const classes = useClasses();
  const dispatch = useDispatch();
  const store = useStore();
  const cellRef = useRef(null);
  const deckAI = useDeckAIComms();

  // Memoized per Cell instance so each cell's (x, y) gets its own single-entry
  // cache. Creating it inside the render body would rebuild the selector — and
  // throw away its memoization cache — on every render, defeating the purpose.
  const selectMergedCell = useMemo(
    () =>
      createSelector([(state) => state.gridd.present.structure.merged, (_, x) => x, (_, __, y) => y], (merged, x, y) =>
        merged.find((m) => m.cell.x === x && m.cell.y === y)
      ),
    []
  );

  // These drive render output — must stay as subscriptions.
  const style = useSelector((state) => state.gridd.present.cellStyles[props.cell.id]);
  const merged = useSelector((state) => selectMergedCell(state, props.x, props.y));
  const cols = useSelector((state) => state.gridd.present.structure.columns);
  const rows = useSelector((state) => state.gridd.present.structure.rows);
  const shapeGroups = useSelector((state) => state.gridd.present.cellStyles.shapeGroups);
  const autofitDebug = useSelector((state) => state.preferences.autofitDebug);
  const cellOverflow = useSelector((state) => (autofitDebug ? state.app.cellOverflow[props.cell.id] : undefined));

  // selection, borders, and cells are only read inside event handlers, never
  // used in render output. Reading them via useStore at dispatch time avoids
  // subscribing every Cell instance to the most volatile slices of state,
  // which previously caused all ~400 cells to re-render on every hover/click.

  function getDimensions() {
    if (merged?.dir === "right") {
      const pins = getPinsForMoving(cols, window.innerWidth);
      const width = pins[merged.cell.y + merged.length + 1] - pins[merged.cell.y];
      const percentage = (width / (pins[merged.cell.y + 1] - pins[merged.cell.y])) * 100;
      return { width: percentage + "%" };
    } else if (merged?.dir === "down") {
      const pins = getPinsForMoving(rows, window.innerHeight);
      const height = pins[merged.cell.x + merged.length + 1] - pins[merged.cell.x];
      const percentage = (height / (pins[merged.cell.x + 1] - pins[merged.cell.x])) * 100;
      return { height: percentage + "%" };
    }

    return {
      width: merged && merged.dir === "right" ? merged.length + 1 + "00%" : undefined,
      height: merged && merged.dir === "down" ? merged.length + 1 + "00%" : undefined,
    };
  }

  function dispatchSelect(e, selectionType, doNormalize = true) {
    const { cells } = store.getState().gridd.present.structure;
    dispatch(
      select({
        cell: { id: props.cell.id, x: props.x, y: props.y },
        type: selectionType,
        cells,
      })
    );
    if (doNormalize) {
      dispatch(normalize({ colCount: cols.length, rowCount: rows.length, cells, isAdditive: e.ctrlKey || e.metaKey }));
    }
    deckAI.sendAction({
      cell: { id: props.cell.id, x: props.x, y: props.y },
      type: selectionType,
      text: props.cell.text,
    });
  }

  return (
    <div
      id={`cell_${props.x}_${props.y}`}
      ref={cellRef}
      tabIndex={0}
      className={classes.button}
      style={{
        background:
          style?.background === "transparent" || !style?.background ? "white" : (style?.background ?? "white"),
        ...getDimensions(),
        zIndex: merged ? 4 : undefined,
        //overflow: "hidden",
      }}
      onClick={(e) => {
        if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
          const { borders } = store.getState().gridd.present;
          const borderFound = findBorderOnClick(isMouseClickedOnBorder(e, cellRef.current), borders, props);
          if (borderFound) {
            dispatch(setLastOpenAccordionItem("preset"));
            dispatch(select({ border: borderFound }));
            return;
          } else {
            const clickedOnBorder = isMouseClickedOnBorder(e, cellRef.current);
            if (clickedOnBorder.isOnBorder) {
              dispatch(
                select({
                  slot: {
                    vertical: clickedOnBorder.isOnLeftBorder || clickedOnBorder.isOnRightBorder,
                    index:
                      clickedOnBorder.isOnLeftBorder || clickedOnBorder.isOnTopBorder ? props.index : props.index + 1,
                  },
                })
              );
            }
          }
        }
        const selectionType = e.ctrlKey || e.metaKey ? "additive" : e.shiftKey ? "box" : "clear";
        dispatchSelect(e, selectionType);
      }}
      onContextMenu={(e) => {
        const selection = store.getState().selection;
        if (
          selection.columns.length < 1 &&
          selection.rows.length < 1 &&
          (selection.cells.length <= 1 || selection.slot || selection.borders.length > 0)
        ) {
          const selectionType = e.shiftKey ? "box" : e.ctrlKey || e.metaKey ? "additive" : "clear";
          dispatchSelect(e, selectionType);
        }
      }}
      onMouseEnter={() => {
        if (store.getState().selection.borders.length === 0) {
          dispatch(hover({ cell: { id: props.cell.id, x: props.x, y: props.y } }));
        }
      }}
      onMouseLeave={() => {
        if (store.getState().selection.borders.length === 0) {
          dispatch(hover({ cell: {} }));
        }
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        {autofitDebug &&
          cellOverflow &&
          props.cell.text &&
          cols[props.y]?.[0] !== "s" &&
          rows[props.x]?.[0] !== "s" && (
            <div
              style={{
                position: "absolute",
                top: 2,
                right: 2,
                zIndex: 9999,
                background: "rgba(0,0,0,0.6)",
                color: "white",
                fontSize: 9,
                padding: "1px 3px",
                borderRadius: 3,
                pointerEvents: "none",
                whiteSpace: "nowrap",
              }}
            >
              {Math.round(cellOverflow.minimumWidth ?? 0)} x {Math.round(cellOverflow.contentHeight ?? 0)} (
              {Math.round(cellOverflow.contentWidth ?? 0)})
            </div>
          )}
        {style?.img ? <CellCustomImage {...style.img} /> : <></>}
        {style?.shape?.type ? (
          <CellShape {...style.shape} cellId={props.cell.id}>
            <CellTextWithIcon
              id={props.cell.id}
              x={props.x}
              y={props.y}
              text={props.cell.text}
              json={props.cell.json}
              style={style}
            />
          </CellShape>
        ) : style?.shapeGroupId && shapeGroups ? (
          <CellShapeElement
            cellId={props.cell.id}
            groupId={style.shapeGroupId}
            x={props.x}
            y={props.y}
            {...shapeGroups[style.shapeGroupId]}
            fill={style?.shape?.color ?? shapeGroups[style.shapeGroupId]?.fill}
            outline={style?.shape?.outline ?? shapeGroups[style.shapeGroupId]?.outline}
            outlineWeight={style?.shape?.outlineWeight ?? shapeGroups[style.shapeGroupId]?.outlineWeight}
            outlineColor={style?.shape?.outlineColor ?? shapeGroups[style.shapeGroupId]?.outlineColor}
          >
            <CellTextWithIcon
              id={props.cell.id}
              x={props.x}
              y={props.y}
              text={props.cell.text}
              json={props.cell.json}
              style={style}
            />
          </CellShapeElement>
        ) : (
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              zIndex: style?.iconStyle?.zIndex ?? 0,
            }}
          >
            <CellTextWithIcon
              id={props.cell.id}
              x={props.x}
              y={props.y}
              text={props.cell.text}
              json={props.cell.json}
              style={style}
            />
          </div>
        )}
      </div>
    </div>
  );
};

Cell.propTypes = {
  cell: PropTypes.object.isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

export default React.memo(Cell);
